import {
  AccordionSummary,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryIcon from "@mui/icons-material/History";
import { useTranslation } from "hhn-commons";
import { getAgeFromDob } from "@home-health-notify/domain";
import { IRecentMRNLookup } from "@home-health-notify/domain/build/chat/emr/IEMRData";

import { UserAvatar } from "../common/UserAvatar";
import {
  RecentMRNAccordion,
  RecentMRNCakeIcon,
  RecentMRNDetails,
  RecentMRNItemButton,
  RecentMRNMeta,
  RecentMRNLoader,
  RecentMRNName,
  RecentMRNPrimary,
  RecentMRNSummaryContent,
} from "./AddPatientGroup.style";

type RecentMRNListProps = {
  recentMRNs: IRecentMRNLookup[];
  expanded: boolean;
  loading?: boolean;
  onToggle: (expanded: boolean) => void;
  onSelect: (mrn: string) => void;
};

export const RecentMRNList = ({
  recentMRNs,
  expanded,
  loading = false,
  onToggle,
  onSelect,
}: RecentMRNListProps) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "patientGroup" });

  if (!loading && !recentMRNs.length) {
    return null;
  }

  return (
    <RecentMRNAccordion
      expanded={expanded}
      onChange={(_, isExpanded) => onToggle(isExpanded)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="recent-mrns-content"
        id="recent-mrns-header"
      >
        <RecentMRNSummaryContent>
          <HistoryIcon fontSize="small" color="action" />
          <Typography>{tNs("recentMRNsTitle")}</Typography>
        </RecentMRNSummaryContent>
      </AccordionSummary>
      <RecentMRNDetails>
        {loading ? (
          <RecentMRNLoader>
            <CircularProgress size={24} />
          </RecentMRNLoader>
        ) : (
          <List disablePadding>
            {recentMRNs.map(item => {
              const fullName = `${item.firstName} ${item.lastName}`;
              const age = getAgeFromDob(item.dob);
              return (
                <ListItem key={item.mrn} disablePadding>
                  <RecentMRNItemButton
                    data-testid={`recent-mrn-${item.mrn}`}
                    onClick={() => onSelect(item.mrn)}
                  >
                    <ListItemAvatar>
                      <UserAvatar alt={fullName} avatarSize={38} />
                    </ListItemAvatar>
                    <ListItemText
                      disableTypography
                      primary={
                        <RecentMRNPrimary>
                          <RecentMRNName>{`${fullName} - ${item.mrn}`}</RecentMRNName>
                        </RecentMRNPrimary>
                      }
                      secondary={
                        <RecentMRNMeta>
                          <RecentMRNCakeIcon />
                          <span>{`${item.dob} (${age} ${
                            age === 1 ? "yr" : "yrs"
                          })`}</span>
                        </RecentMRNMeta>
                      }
                    />
                  </RecentMRNItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </RecentMRNDetails>
    </RecentMRNAccordion>
  );
};
